import {
  CheckCircleOutlined,
  DashboardOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  FolderAddOutlined,
  HistoryOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Table, Tag, Space, Tooltip, Pagination, Avatar, Popover, Button, Dropdown, Menu } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DeletePositionApi from "../../api/positions/DeletePositionApi";
import DisablePositionApi from "../../api/positions/DisablePositionApi";
import EnablePositionApi from "../../api/positions/EnablePositionApi";
import RemovePositionUserApi from "../../api/positions/RemovePositionUserApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";
import AddPositionUserModal from "../modal/add/AddPositionUserModal";
import ConfirmationModal from "../modal/ConfirmationModal";
import EditPositionModal from "../modal/edit/EditPositionModal";
import EditPositionUserModal from "../modal/edit/EditPositionUserModal";
import HardDeleteModal from "../modal/HardDeleteModal";
import PositionTimelineModal from "../modal/info/PositionTimelineModal";
import { LIMIT_PER_PAGE } from "../../helpers/constants";
import MetabaseCodeModal from "../modal/MetabaseCodeModal";
import UpdateMetabaseCodeApi from "../../api/metabase/UpdateMetabaseCodeApi.js";
import MetabaseCodeInfoModal from "../modal/MetabaseCodeInfoModal.js";

function PositionsTable(props) {
  const [addUserModal, setAddUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState();
  const [disableModal, setDisableModal] = useState(false);
  const [enableModal, setEnableModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState("");
  const [removeUserModal, setRemoveUserModal] = useState(false);
  const [timelineModal, setTimelineModal] = useState(false);
  const [metabaseModal, setMetabaseModal] = useState(false);
  const [metabaseInfoModal, setMetabaseInfoModal] = useState(false);
  const [data, setData] = useState();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  useEffect(() => {
    setLoading(props.loading);
    setData(props.data);
  }, [props.loading]);

  /*If page changed from parent component */
  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  async function deletePosition() {
    const apiResponse = await DeletePositionApi(selectedPosition?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      if (data?.positions?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setSelectedPosition();
    setDeleteModal("");
  }

  async function removeUser() {
    const apiResponse = await RemovePositionUserApi(selectedPosition?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      if (data?.positions?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setSelectedPosition();
    setRemoveUserModal(false);
  }

  async function disablePosition() {
    const apiResponse = await DisablePositionApi(selectedPosition?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      props.changePage(page);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't disable. Try again later."
      );
    }
    setSelectedPosition();
    setDisableModal(false);
  }

  async function enablePosition() {
    const apiResponse = await EnablePositionApi(selectedPosition?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      props.changePage(page);
    } else {
      openNotificationError("bottomRight", "Couldn't enable. Try again later.");
    }
    setSelectedPosition();
    setEnableModal(false);
  }

  async function updateMetabaseCode(values) {
    const apiResponse = await UpdateMetabaseCodeApi(
      selectedPosition?.id,
      values.payload,
      values.cycle
    );
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
    } else {
      openNotificationError("bottomRight", "Couldn't enable. Try again later.");
    }
    setSelectedPosition();
    setEnableModal(false);
  }

  function statusColor(status) {
    let output = "green";
    if (status === "Invited") {
      output = "orange";
    } else if (status === "Disabled") {
      output = "red";
    }
    return output;
  }

  const columns = [
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      sorter: (a, b) => {
        const aName = a.designation?.name || "";
        const bName = b.designation?.name || "";
        return aName.localeCompare(bName);
      },
      render: (text, record) => (
        <div>
          {record.designation?.abbreviation || record.designation?.name || "-"}
        </div>
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      sorter: (a, b) => {
        const aName = a.unit?.name || "";
        const bName = b.unit?.name || "";
        return aName.localeCompare(bName);
      },
      filters: [
        ...new Set(data?.positions?.map(p => p.unit?.name).filter(Boolean))
      ].map(unit => ({ text: unit, value: unit })),
      onFilter: (value, record) => record.unit?.name === value,
      render: (text, record) => <div>{record.unit?.name || "-"}</div>,
    },
    {
      title: "Role",
      dataIndex: "userRole",
      key: "userRole",
      sorter: (a, b) => {
        const aRole = a.userRole?.name || "";
        const bRole = b.userRole?.name || "";
        return aRole.localeCompare(bRole);
      },
      filters: [
        ...new Set(data?.positions?.map(p => p.userRole?.name).filter(Boolean))
      ].map(role => ({ text: role, value: role })),
      onFilter: (value, record) => record.userRole?.name === value,
      render: (text, record) => (
        <div className="flex items-center">
          <div className="mr-1">{record.userRole?.name}</div>
          {permissionAbility(userLevel, "positions", "edit") && (
            <Tooltip title={"Change role"}>
              <div
                onClick={(event) => {
                  setEditModal(true);
                  setSelectedPosition(record);
                }}
                className="text-blue-500 hover:text-blue-600 cursor-pointer inline text-xs mr-1"
              >
                <EditOutlined />
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (text, record) =>
        record.user?.id ? (
          <div className="flex items-center">
            {record.user?.status !== "Invited" &&
            record.user?.status !== "Pending" ? (
              <div className="flex items-center">
                {record.user?.imageUrl ? (
                  <Avatar src={record.user?.imageUrl} />
                ) : (
                  <Avatar>
                    {record.user?.firstName?.charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <div className="mx-1">
                  <div className="max-w-xs truncate overflow-hidden">
                    {record.user?.firstName + " " + record.user?.lastName}
                  </div>
                  {/* <div className="text-xs -mt-1">Name</div> */}
                </div>
              </div>
            ) : (
              <div className="max-w-xs truncate overflow-hidden mr-1">
                {record.user?.email}
              </div>
            )}
            {permissionAbility(userLevel, "users", "delete") &&
              positionState.id !== record.id && (
                <Tooltip title={"Remove user"}>
                  <div
                    onClick={(event) => {
                      setRemoveUserModal(true);
                      setSelectedPosition(record);
                    }}
                    className="text-red-500 hover:text-red-600 cursor-pointer inline text-xs"
                  >
                    <DeleteOutlined />
                  </div>
                </Tooltip>
              )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      sorter: (a, b) => {
        const aStatus = a.status || "";
        const bStatus = b.status || "";
        return aStatus.localeCompare(bStatus);
      },
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Inactive', value: 'Inactive' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <Tag color={statusColor(text)}>{text?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          <Tooltip title={"Timeline"}>
            <div
              onClick={() => {
                setTimelineModal(true);
                setSelectedPosition(record);
              }}
              className="text-blue-500 hover:text-blue-600 cursor-pointer"
            >
              <HistoryOutlined />
            </div>
          </Tooltip>
          {permissionAbility(userLevel, "positions", "edit") &&
            record.status === "Active" && (
              <Tooltip title={"Metabase Code"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setMetabaseInfoModal(true);
                    setSelectedPosition(record);
                  }}
                  className="text-blue-500 hover:text-blue-600 cursor-pointer"
                >
                  <DashboardOutlined />
                </div>
              </Tooltip>
            )}
          {permissionAbility(userLevel, "positions", "edit") &&
            record.status === "Active" && (
              <Tooltip title={"Disable"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setDisableModal(true);
                    setSelectedPosition(record);
                  }}
                  className="text-orange-500 hover:text-orange-600 cursor-pointer"
                >
                  <StopOutlined />
                </div>
              </Tooltip>
            )}
          {permissionAbility(userLevel, "positions", "edit") &&
            record.status === "Disabled" && (
              <Tooltip title={"Enable"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setEnableModal(true);
                    setSelectedPosition(record);
                  }}
                  className="text-green-500 hover:text-green-600 cursor-pointer"
                >
                  <CheckCircleOutlined />
                </div>
              </Tooltip>
            )}
          {permissionAbility(userLevel, "positions", "delete") &&
          record.canDelete ? (
            <Tooltip title={"Delete"}>
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteModal("delete");
                  setSelectedPosition(record);
                }}
                className="text-red-500 hover:text-red-600 cursor-pointer"
              >
                <DeleteOutlined />
              </div>
            </Tooltip>
          ) : record.canArchive ? (
            <Tooltip title={"Archive"}>
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteModal("archive");
                  setSelectedPosition(record);
                }}
                className="text-red-500 hover:text-red-600 cursor-pointer"
              >
                <FolderAddOutlined />
              </div>
            </Tooltip>
          ) : (
            <Popover
              placement="left"
              content={"This position can't be deleted"}
              trigger="hover"
            >
              <div className="text-gray-500">
                <FolderAddOutlined />
              </div>
            </Popover>
          )}
        </Space>
      ),
    },
  ];
  console.log(data?.positions);
  return (
    <>
      <Table
        columns={columns}
        dataSource={data?.positions}
        loading={loading}
        pagination={false}
      />
      <div className="text-center mt-6">
        <Pagination
          onChange={(num) => {
            props.changePage(num);
            setPage(num);
          }}
          current={page}
          total={data?.totalCount || 0}
          pageSize={LIMIT_PER_PAGE}
        />
      </div>
      <ConfirmationModal
        visible={disableModal}
        handleCancel={() => {
          setDisableModal(false);
          setSelectedPosition();
        }}
        handleOk={() => disablePosition()}
        text="Are you sure want to disable this position?"
        title="Disable position"
      />
      <ConfirmationModal
        visible={enableModal}
        handleCancel={() => {
          setEnableModal(false);
          setSelectedPosition();
        }}
        handleOk={() => enablePosition()}
        text="Are you sure want to enable this position?"
        title="Enable position"
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => {
          setDeleteModal("");
          setSelectedPosition();
        }}
        handleOk={() => deletePosition()}
        text={`Are you sure want to ${deleteModal} this position?`}
        title={deleteModal?.charAt(0).toUpperCase() + deleteModal?.slice(1)}
      />
      <HardDeleteModal
        visible={removeUserModal}
        handleCancel={() => {
          setRemoveUserModal(false);
          setSelectedPosition();
        }}
        handleOk={() => removeUser()}
        text="Are you sure want to remove this user from this position?"
        title="Remove user"
      />
      <EditPositionModal
        visible={editModal}
        handleOk={() => props.changePage(page)}
        handleCancel={() => {
          setEditModal(false);
          setSelectedPosition();
        }}
        position={selectedPosition}
      />
      <AddPositionUserModal
        visible={addUserModal}
        handleCancel={() => setAddUserModal(false)}
      />
      <EditPositionUserModal
        visible={editUserModal}
        handleCancel={() => setEditUserModal(false)}
      />
      <PositionTimelineModal
        visible={timelineModal}
        handleCancel={() => {
          setTimelineModal(false);
          setSelectedPosition();
        }}
        position={selectedPosition}
      />
      <MetabaseCodeModal
        isOpen={metabaseModal}
        onClose={() => setMetabaseModal(false)}
        onSubmit={updateMetabaseCode}
        selectedPosition={selectedPosition}
      />
      <MetabaseCodeInfoModal
        isOpen={metabaseInfoModal}
        onClose={() => setMetabaseInfoModal(false)}
        onAdd={() => {
          setMetabaseModal(true);
          setMetabaseInfoModal(false);
        }}
        selectedPosition={selectedPosition}
      />
    </>
  );
}

export default PositionsTable;
